import axios from "axios";
import axiosInstance from "./axiosInstance";

export const getRegisteredUsers= async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/registered`);
    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

// Bulk accommodation checkout for all MSPs in a specific cohort
export const bulkCheckoutAccommodationCohort = async (
  { cohortTag, endDate } = {},
  token
) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/accommodation/checkout-cohort`,
      {
        cohortTag,
        ...(endDate ? { endDate } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("BULK ACCOMMODATION COHORT CHECKOUT ERROR", error);
    return error?.response;
  }
};

// ============================================
// OPTIMIZED LITE ENDPOINTS FOR 2G PERFORMANCE
// ============================================

/**
 * Get users with minimal fields - excludes photos, fingerprints
 * Reduces payload by ~80% for faster loading on slow networks
 */
export const getUsersLite = async ({ 
  term, 
  page = 1, 
  disability, 
  gender, 
  state, 
  lga, 
  community, 
  religion, 
  physicalFitness,
  sortBy,
  operator,
  sortOrder
} = {}) => {
  try {
    let queryString = `?page=${page}`;

    if (term) {
      queryString += `&searchTerm=${encodeURIComponent(term)}`;
    }

    const filters = [
      { key: 'disability', value: disability },
      { key: 'gender', value: gender },
      { key: 'state', value: state },
      { key: 'lga', value: lga },
      { key: 'community', value: community },
      { key: 'religion', value: religion },
      { key: 'physicalFitness', value: physicalFitness },
      { key: 'operator', value: operator }
    ];

    filters.forEach(filter => {
      if (filter.value) {
        queryString += `&${filter.key}=${encodeURIComponent(filter.value)}`;
      }
    });

    if (sortBy) {
      queryString += `&sortBy=${encodeURIComponent(sortBy)}`;
      queryString += `&sortOrder=${sortOrder || 'asc'}`;
    }

    // Use axiosInstance with retry logic
    const res = await axiosInstance.get(`/user/lite${queryString}`);
    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

/**
 * Combined dashboard endpoint - single request instead of 4 calls
 * Returns: totalUsers, registeredUsers, campStatus, infantsCount
 */
export const getDashboardData = async ({ 
  date,
  cohortTag,
  issamCohortYear,
  state,
  lga,
  community,
  disability,
  gender,
  religion,
  physicalFitness,
  operator,
  mspType,
  registeredUsersOnly = "true",
  youthInWorkOnly,
  workEnablingOutreachOnly,
} = {}) => {
  try {
    // Use URLSearchParams for proper query string construction
    const params = new URLSearchParams();
    params.append(
      "registeredUsersOnly",
      registeredUsersOnly === undefined || registeredUsersOnly === null
        ? "true"
        : String(registeredUsersOnly)
    );
    if (youthInWorkOnly !== undefined && youthInWorkOnly !== null && youthInWorkOnly !== "") {
      params.append("youthInWorkOnly", String(youthInWorkOnly));
    }
    if (workEnablingOutreachOnly !== undefined && workEnablingOutreachOnly !== null && workEnablingOutreachOnly !== "") {
      params.append("workEnablingOutreachOnly", String(workEnablingOutreachOnly));
    }
    
    if (date) params.append('date', date);
    
    // Add filter parameters
    const filters = [
      { key: 'cohortTag', value: cohortTag },
      { key: 'issamCohortYear', value: issamCohortYear },
      { key: 'state', value: state },
      { key: 'lga', value: lga },
      { key: 'community', value: community },
      { key: 'disability', value: disability },
      { key: 'gender', value: gender },
      { key: 'religion', value: religion },
      { key: 'physicalFitness', value: physicalFitness },
      { key: 'operator', value: operator },
      { key: 'mspType', value: mspType },
    ];
    
    filters.forEach(filter => {
      if (filter.value) {
        params.append(filter.key, filter.value);
      }
    });
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const res = await axiosInstance.get(`/user/dashboard${queryString}`);
    return res;
  } catch (error) {
    console.log("DASHBOARD ERROR", error);
    return error?.response;
  }
};

export const getUsers = async ({ 
  term, 
  type,           // legacy searchType param
  searchType,     // preferred searchType param
  page = 1, 
  disability, 
  gender, 
  sex,            // legacy alias for gender
  state, 
  lga, 
  community, 
  religion, 
  physicalFitness,
  sortBy,
  operator,
  sortOrder
} = {}) => {
  try {
    const token = localStorage.getItem('token');
    
    // Start building query string with page
    let queryString = `?page=${page}`;

    // Add search term if provided (general search across multiple fields)
    const effectiveSearchType = searchType || type;
    if (term) {
      queryString += `&searchTerm=${encodeURIComponent(term)}`;
      if (effectiveSearchType) {
        queryString += `&searchType=${encodeURIComponent(effectiveSearchType)}`;
      }
    }

    // Add filters
    const genderValue = gender || sex;
    const filters = [
      { key: 'disability', value: disability },
      { key: 'gender', value: genderValue },
      { key: 'state', value: state },
      { key: 'lga', value: lga },
      { key: 'community', value: community },
      { key: 'religion', value: religion },
      { key: 'physicalFitness', value: physicalFitness },
      { key: 'operator', value: operator }
    ];

    // Append non-empty filters
    filters.forEach(filter => {
      if (filter.value) {
        queryString += `&${filter.key}=${encodeURIComponent(filter.value)}`;
      }
    });

    // Add sorting if provided
    if (sortBy) {
      queryString += `&sortBy=${encodeURIComponent(sortBy)}`;
      queryString += `&sortOrder=${sortOrder || 'asc'}`;
    }

    // Make API call with authentication
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user${queryString}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const getUsersNumbers = async (
 { term, 
  type,
  searchType,
  page = 1, 
  disability, 
  gender, 
  sex,
  idpClassification,
  cohortTag,
  issamCohortYear,
  state, 
  lga, 
  ward,
  community, 
  religion, 
  physicalFitness,
  mspType,
  sortBy,
  sortOrder,
  startDate,
  endDate,
  dateFilterType,
  registeredUsersOnly = "true",
  youthInWorkOnly,
  workEnablingOutreachOnly,
}
) => {
  console.log("Function called with parameters:", {
    term, type, page, disability, gender, cohortTag, state, lga, community, 
    religion, physicalFitness, sortBy, sortOrder, startDate, endDate, dateFilterType,
    youthInWorkOnly,
    workEnablingOutreachOnly,
  });
  
  try {
   const token = localStorage.getItem('token');
   
   const registeredOnly =
     registeredUsersOnly === undefined || registeredUsersOnly === null
       ? "true"
       : String(registeredUsersOnly);

   // Start building query string with page + scope flag (always send explicitly)
   let queryString = `?page=${page}&registeredUsersOnly=${encodeURIComponent(registeredOnly)}`;
   if (youthInWorkOnly !== undefined && youthInWorkOnly !== null && youthInWorkOnly !== "") {
     queryString += `&youthInWorkOnly=${encodeURIComponent(String(youthInWorkOnly))}`;
   }
   if (workEnablingOutreachOnly !== undefined && workEnablingOutreachOnly !== null && workEnablingOutreachOnly !== "") {
     queryString += `&workEnablingOutreachOnly=${encodeURIComponent(String(workEnablingOutreachOnly))}`;
   }

   // Add search term if provided (general search across multiple fields)
   const effectiveSearchType = searchType || type;
   if (term) {
     queryString += `&searchTerm=${encodeURIComponent(term)}`;
     if (effectiveSearchType) {
       queryString += `&searchType=${encodeURIComponent(effectiveSearchType)}`;
     }
   }

   // Add filters
   const genderValue = gender || sex;
   const filters = [
     { key: 'disability', value: disability },
     { key: 'gender', value: genderValue },
     { key: 'idpClassification', value: idpClassification },
     { key: 'cohortTag', value: cohortTag },
     { key: 'issamCohortYear', value: issamCohortYear },
     { key: 'lga', value: lga },
     { key: 'ward', value: ward },
     { key: 'state', value: state },
     { key: 'community', value: community },
     { key: 'religion', value: religion },
     { key: 'physicalFitness', value: physicalFitness },
     { key: 'mspType', value: mspType },
     { key: 'startDate', value: startDate },
     { key: 'endDate', value: endDate },
     { key: 'dateFilterType', value: dateFilterType },
   ];
   
   // Append non-empty filters
   filters.forEach(filter => {
     if (filter.value !== undefined && filter.value !== null && filter.value !== "") {
       queryString += `&${filter.key}=${encodeURIComponent(filter.value)}`;
       console.log(`Added filter: ${filter.key} = ${filter.value}`);
     }
   });

   // Add sorting if provided
   if (sortBy) {
     queryString += `&sortBy=${encodeURIComponent(sortBy)}`;
     queryString += `&sortOrder=${sortOrder || 'asc'}`;
   }

   console.log("Final query string:", queryString);
   console.log("Full URL:", `${process.env.REACT_APP_API_URL}/user/numbers${queryString}`);

   // Make API call with authentication
   const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/numbers${queryString}`, {
     headers: {
       Authorization: `Bearer ${token}`,
     },
   });

   return res;
 } catch (error) {
   console.log("ERROR", error);
    return error?.response;
  }
};

/** All users in DB (no qrCodeUrl / registered-only restriction). */
export const getAllUsersNumbers = async (params = {}) =>
  getUsersNumbers({ ...params, registeredUsersOnly: "false" });

/** Summary stats for all users (not registered-only). */
export const getAllUsersSummaryStats = async (params = {}) =>
  getUsersSummaryStats({ ...params, registeredUsersOnly: "false" });

/** Camp overview for all users (not cohort-scoped to ALLOWED_COHORT). */
export const getAllUsersDashboard = async (params = {}) =>
  getDashboardData({ ...params, registeredUsersOnly: "false" });

export const getUsersSummaryStats = async ({
  term,
  disability,
  gender,
  idpClassification,
  cohortTag,
  issamCohortYear,
  state,
  lga,
  ward,
  community,
  religion,
  physicalFitness,
  mspType,
  operator,
  startDate,
  endDate,
  registeredUsersOnly = "true",
  youthInWorkOnly,
  workEnablingOutreachOnly,
} = {}) => {
  try {
    const token = localStorage.getItem('token');
    
    // Use URLSearchParams for proper query string construction
    const params = new URLSearchParams();
    const registeredOnly =
      registeredUsersOnly === undefined || registeredUsersOnly === null
        ? "true"
        : String(registeredUsersOnly);
    params.append("registeredUsersOnly", registeredOnly);
    if (youthInWorkOnly !== undefined && youthInWorkOnly !== null && youthInWorkOnly !== "") {
      params.append("youthInWorkOnly", String(youthInWorkOnly));
    }
    if (workEnablingOutreachOnly !== undefined && workEnablingOutreachOnly !== null && workEnablingOutreachOnly !== "") {
      params.append("workEnablingOutreachOnly", String(workEnablingOutreachOnly));
    }

    // Add search term if provided
    if (term) {
      params.append('searchTerm', term);
    }

    // Add filters
    const filterParams = [
      { key: 'disability', value: disability },
      { key: 'gender', value: gender },
      { key: 'idpClassification', value: idpClassification },
      { key: 'cohortTag', value: cohortTag },
      { key: 'issamCohortYear', value: issamCohortYear },
      { key: 'state', value: state },
      { key: 'lga', value: lga },
      { key: 'ward', value: ward },
      { key: 'community', value: community },
      { key: 'religion', value: religion },
      { key: 'physicalFitness', value: physicalFitness },
      { key: 'mspType', value: mspType },
      { key: 'operator', value: operator },
      { key: 'startDate', value: startDate },
      { key: 'endDate', value: endDate },
    ];

    filterParams.forEach(filter => {
      if (filter.value !== undefined && filter.value !== null && filter.value !== "") {
        params.append(filter.key, filter.value);
      }
    });

    const queryString = `?${params.toString()}`;

    // Make API call with authentication
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/summary-stats${queryString}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const getTodaysMealRecords = async ({ date } = {}) => {
  try {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/meals${query}`
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};
export const getUser = async (id) => {
  try {
    const token = localStorage.getItem('token');
    // URL-encode the id to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedId = encodeURIComponent(id);
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/${encodedId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};


export const downloadExcel = async ({ 
  term, 
  type,
  searchType,
  disability, 
  gender, 
  sex,
  idpClassification,
  cohortTag,
  issamCohortYear,
  state, 
  lga, 
  ward,
  community, 
  religion, 
  physicalFitness,
  mspType,
  sortBy,
  sortOrder,
  registeredUsersOnly = "false",
  youthInWorkOnly,
  workEnablingOutreachOnly,
  startDate,
  endDate,
  dateFilterType
} = {}) => {
  try {
    const registeredOnly =
      registeredUsersOnly === undefined || registeredUsersOnly === null
        ? "true"
        : String(registeredUsersOnly);

    // Use URLSearchParams for proper query string construction
    const params = new URLSearchParams();
    params.append("registeredUsersOnly", registeredOnly);
    if (youthInWorkOnly !== undefined && youthInWorkOnly !== null && youthInWorkOnly !== "") {
      params.append("youthInWorkOnly", String(youthInWorkOnly));
    }
    if (workEnablingOutreachOnly !== undefined && workEnablingOutreachOnly !== null && workEnablingOutreachOnly !== "") {
      params.append("workEnablingOutreachOnly", String(workEnablingOutreachOnly));
    }

    // Add search term if provided
    const effectiveSearchType = searchType || type;
    if (term) {
      params.append('searchTerm', term);
      if (effectiveSearchType) {
        params.append('searchType', effectiveSearchType);
      }
    }

    // Add filters including date parameters
    const genderValue = gender || sex;
    const filters = [
      { key: 'disability', value: disability },
      { key: 'gender', value: genderValue },
      { key: 'idpClassification', value: idpClassification },
      { key: 'cohortTag', value: cohortTag },
      { key: 'issamCohortYear', value: issamCohortYear },
      { key: 'state', value: state },
      { key: 'lga', value: lga },
      { key: 'ward', value: ward },
      { key: 'community', value: community },
      { key: 'religion', value: religion },
      { key: 'physicalFitness', value: physicalFitness },
      { key: 'mspType', value: mspType },
      { key: 'startDate', value: startDate },
      { key: 'endDate', value: endDate },
      { key: 'dateFilterType', value: dateFilterType }
    ];

    // Append non-empty filters
    filters.forEach(filter => {
      if (filter.value) {
        params.append(filter.key, filter.value);
      }
    });

    // Add sorting if provided
    if (sortBy) {
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder || 'asc');
    }

    // Build query string - only add ? if there are params
    const queryString = params.toString() ? `?${params.toString()}` : '';

    // Get authentication token
    const token = localStorage.getItem('token');
    
    // Make API call with responseType: 'blob' and authentication
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/download${queryString}`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res;
  } catch (error) {
    console.log("ERROR", error);
    
    // Handle blob error responses - convert blob to JSON to get actual error message
    if (error?.response?.data instanceof Blob) {
      try {
        const errorText = await error.response.data.text();
        const errorJson = JSON.parse(errorText);
        console.log("Server error details:", errorJson);
        error.response.errorDetails = errorJson;
      } catch (parseError) {
        console.log("Could not parse error response");
      }
    }
    
    return error?.response;
  }
};


export const searchUser = async (searchTerm) => {
  try {
    const token = localStorage.getItem('token');
    // URL-encode the searchTerm to handle special characters
    const encodedSearchTerm = encodeURIComponent(searchTerm);
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/search?searchTerm=${encodedSearchTerm}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const createUser = async (data) => {
  console.log("skepta", data);

  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/user`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const recordMeal = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/meals`,
      {
        data: "data"
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const recordAttendance = async (userId, subject = null) => {
  try {
    const token = localStorage.getItem('token');
    const encodedUserId = encodeURIComponent(userId);
    const payload = { attended: true };
    if (subject) {
      payload.subject = subject;
    }
    
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/attendance`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

// Schedule APIs
export const getSchedule = async (token, hall = null) => {
  try {
    const url = hall 
      ? `${process.env.REACT_APP_API_URL}/schedule?hall=${encodeURIComponent(hall)}`
      : `${process.env.REACT_APP_API_URL}/schedule`;
    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res;
  } catch (error) {
    console.log("GET SCHEDULE ERROR", error);
    return error?.response;
  }
};

export const getScheduleForHallAndDay = async (hall, dayOfWeek, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/schedule/hall/${encodeURIComponent(hall)}?dayOfWeek=${dayOfWeek}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET SCHEDULE FOR HALL AND DAY ERROR", error);
    return error?.response;
  }
};

export const createOrUpdateSchedule = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/schedule`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE OR UPDATE SCHEDULE ERROR", error);
    return error?.response;
  }
};

export const bulkUpdateSchedule = async (schedules, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/schedule/bulk`,
      { schedules },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("BULK UPDATE SCHEDULE ERROR", error);
    return error?.response;
  }
};

export const deleteSchedule = async (id, token) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/schedule/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE SCHEDULE ERROR", error);
    return error?.response;
  }
};
export const recordAppointment = async (userId, data) => {
  try {
    const token = localStorage.getItem('token');
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/health`, 
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const updateUser = async (id, data) => {
  console.log("skepta", data);

  try {
    const token = localStorage.getItem('token');
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/user/${id}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

// Generate userId after fingerprints have been uploaded
export const generateUserId = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${id}/generate-userid`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};

export const deleteEvent = async (token, id) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const res = await axios.delete(
      `${process.env.NEXT_PUBLIC_URL}/events/${id}`,
      config
    );

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};


export const login = async (
  data
) => {

  
  
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/login`, data);

    return res;
  } catch (error) {
    console.log("ERROR", error);
    return error?.response;
  }
};


// Check-in tablet API request
export const checkInTablet = async (userId) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/${encodedUserId}/tablet-checkin`, {
     message: "hello"
    });

    return res;
  } catch (error) {
    console.log("CHECK-IN ERROR", error);
    return error?.response;
  }
};

// Check-out tablet API request
export const checkOutTablet = async (userId, checkoutData = {}) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/${encodedUserId}/tablet-checkout`, {
          message: "hello"

    });

    return res;
  } catch (error) {
    console.log("CHECK-OUT ERROR", error);
    return error?.response;
  }
};

export const checkInCamp = async (userId, token) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/camp-checkin`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("CAMP CHECK-IN ERROR", error);
    return error?.response;
  }
};

export const checkOutCamp = async (userId, token) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/camp-checkout`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("CAMP CHECK-OUT ERROR", error);
    return error?.response;
  }
};

export const decampCamp = async (userId, token) => {
  try {
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/camp-decamp`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("CAMP DECAMP ERROR", error);
    return error?.response;
  }
};

export const getCampAttendanceSummary = async ({ date, token } = {}) => {
  try {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/camp/summary${query}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("CAMP SUMMARY ERROR", error);
    return error?.response;
  }
};

export const getMealHistory = async ({ userId, token }) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/meal-history`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return res;
  } catch (error) {
    console.log("MEAL HISTORY ERROR", error);
    return error?.response;
  }
};

export const getAccommodationRooms = async (token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("ACCOMMODATION ROOMS ERROR", error);
    return error?.response;
  }
};

export const createAccommodationRoom = async (payload, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE ROOM ERROR", error);
    return error?.response;
  }
};

export const updateAccommodationRoom = async (roomId, payload, token) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms/${roomId}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE ROOM ERROR", error);
    return error?.response;
  }
};

export const deleteAccommodationRoom = async (roomId, token) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms/${roomId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE ROOM ERROR", error);
    return error?.response;
  }
};

export const updateAccommodationBunk = async (
  roomId,
  payload,
  token
) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms/${roomId}/assignment`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE BUNK ERROR", error);
    return error?.response;
  }
};

export const createRoomIssue = async (roomId, payload, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms/${roomId}/issues`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE ROOM ISSUE ERROR", error);
    return error?.response;
  }
};

export const getRoomIssues = async (roomId, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms/${roomId}/issues`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET ROOM ISSUES ERROR", error);
    return error?.response;
  }
};

export const resolveRoomIssue = async (roomId, issueId, token) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/accommodation/rooms/${roomId}/issues/${issueId}/resolve`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("RESOLVE ROOM ISSUE ERROR", error);
    return error?.response;
  }
};

export const getAccommodationStats = async (token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/accommodation/stats`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("ACCOMMODATION STATS ERROR", error);
    return error?.response;
  }
};

export const getCampStatusList = async ({ date, token, ...filters }) => {
  try {
    const params = new URLSearchParams();
    if (date) params.append("date", date);
    
    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== "token") {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/camp/status${query}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CAMP STATUS ERROR", error);
    return error?.response;
  }
};

export const getAuditLogs = async ({ token, params = {} }) => {
  try {
    const query = new URLSearchParams(params).toString();
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/audit${query ? `?${query}` : ""}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("AUDIT LOG ERROR", error);
    return error?.response;
  }
};

export const getUserAccommodationLogs = async (userId, token) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/audit/user/${encodedUserId}/accommodation`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("USER ACCOMMODATION LOGS ERROR", error);
    return error?.response;
  }
};

// Get tablet status API request
export const getTabletStatus = async (userId) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/${encodedUserId}/tablet/status`);

    return res;
  } catch (error) {
    console.log("GET STATUS ERROR", error);
    return error?.response;
  }
};

// Get user's tablet history API request
export const getTabletHistory = async (userId) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/${encodedUserId}/tablet/history`);

    return res;
  } catch (error) {
    console.log("GET HISTORY ERROR", error);
    return error?.response;
  }
};

// Bulk check-in tablets for multiple users
export const bulkCheckInTablets = async (checkInData) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/tablets/bulk-checkin`, {
      checkIns: checkInData // Array of { userId, tabletId, assignedBy }
    });

    return res;
  } catch (error) {
    console.log("BULK CHECK-IN ERROR", error);
    return error?.response;
  }
};

// Bulk check-out tablets for multiple users
export const bulkCheckOutTablets = async (checkOutData) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/tablets/bulk-checkout`, {
      checkOuts: checkOutData // Array of { userId, condition, notes }
    });

    return res;
  } catch (error) {
    console.log("BULK CHECK-OUT ERROR", error);
    return error?.response;
  }
};

// Staff APIs
export const createStaff = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE STAFF ERROR", error);
    return error?.response;
  }
};

export const getStaff = async (id, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET STAFF ERROR", error);
    return error?.response;
  }
};

export const getAllStaff = async ({ token, page = 1, limit = 50, search, company, sortBy = 'createdAt', sortOrder = 'desc' } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (search) params.append('search', search);
    if (company) params.append('company', company);
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET ALL STAFF ERROR", error);
    return error?.response;
  }
};

export const updateStaff = async (id, data, token) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/staff/${id}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE STAFF ERROR", error);
    return error?.response;
  }
};

export const deleteStaff = async (id, token) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/staff/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE STAFF ERROR", error);
    return error?.response;
  }
};

// Bulk toggle staff to/from camp
export const bulkUpdateStaffToCamp = async (staffIds, toCamp, token) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/staff/bulk-update-to-camp`,
      { staffIds, toCamp },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("BULK UPDATE STAFF TO CAMP ERROR", error);
    return error?.response;
  }
};

// Public - staff for camp (no auth required)
export const getStaffForCamp = async ({ page = 1, limit = 100, search, company, sortBy = 'name', sortOrder = 'asc' } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (search) params.append('search', search);
    if (company) params.append('company', company);
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff/for-camp?${params.toString()}`
    );
    return res;
  } catch (error) {
    console.log("GET STAFF FOR CAMP ERROR", error);
    return error?.response;
  }
};

// Get a single staff schedule
export const getStaffSchedule = async (id, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff-schedule/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET STAFF SCHEDULE ERROR", error);
    return error?.response;
  }
};

// Hall Assignment APIs
export const getCommunities = async (token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/hall/communities`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET COMMUNITIES ERROR", error);
    return error?.response;
  }
};

export const getUsersByCommunity = async (community, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/hall/users?community=${encodeURIComponent(community)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET USERS BY COMMUNITY ERROR", error);
    return error?.response;
  }
};

export const bulkAssignHall = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/hall/assign`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("BULK ASSIGN HALL ERROR", error);
    return error?.response;
  }
};

export const assignHallByPhoneNumbers = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/hall/assign-by-phones`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("ASSIGN HALL BY PHONE NUMBERS ERROR", error);
    return error?.response;
  }
};

export const getHallDistribution = async (token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/hall/distribution`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET HALL DISTRIBUTION ERROR", error);
    return error?.response;
  }
};

export const getCommunitiesByState = async (state, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/hall/communities?state=${encodeURIComponent(state)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET COMMUNITIES BY STATE ERROR", error);
    return error?.response;
  }
};

export const getUsersByHall = async (hall, page = 1, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/hall/hall/${encodeURIComponent(hall)}/users?page=${page}&limit=50`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET USERS BY HALL ERROR", error);
    return error?.response;
  }
};

export const getUsersNotInHall = async (hall, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/hall/hall/${encodeURIComponent(hall)}/users-not-present`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET USERS NOT IN HALL ERROR", error);
    return error?.response;
  }
};

export const reassignHall = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/hall/reassign`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("REASSIGN HALL ERROR", error);
    return error?.response;
  }
};

export const bulkUnassignHall = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/hall/unassign`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("BULK UNASSIGN HALL ERROR", error);
    return error?.response;
  }
};

export const unassignHallByPhoneNumbers = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/hall/unassign-by-phones`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UNASSIGN HALL BY PHONE NUMBERS ERROR", error);
    return error?.response;
  }
};

// Medical Inventory API functions
export const createMedicalInventory = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/medical-inventory`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE MEDICAL INVENTORY ERROR", error);
    return error?.response;
  }
};

export const getAllMedicalInventory = async (params = {}, token) => {
  try {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
        queryParams.append(key, params[key]);
      }
    });
    
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/medical-inventory?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET MEDICAL INVENTORY ERROR", error);
    return error?.response;
  }
};

export const getMedicalInventory = async (inventoryId, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/medical-inventory/${inventoryId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET MEDICAL INVENTORY ERROR", error);
    return error?.response;
  }
};

export const updateMedicalInventory = async (inventoryId, data, token) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/medical-inventory/${inventoryId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE MEDICAL INVENTORY ERROR", error);
    return error?.response;
  }
};

export const deleteMedicalInventory = async (inventoryId, token) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/medical-inventory/${inventoryId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE MEDICAL INVENTORY ERROR", error);
    return error?.response;
  }
};

export const getAvailableDrugs = async (search = "", token) => {
  try {
    const queryParams = search ? `?search=${encodeURIComponent(search)}` : "";
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/medical-inventory/available${queryParams}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET AVAILABLE DRUGS ERROR", error);
    return error?.response;
  }
};

export const getMedicalInventorySummary = async (token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/medical-inventory/summary`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET MEDICAL INVENTORY SUMMARY ERROR", error);
    return error?.response;
  }
};

export const adjustMedicalStock = async (inventoryId, data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/medical-inventory/${inventoryId}/adjust-stock`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("ADJUST MEDICAL STOCK ERROR", error);
    return error?.response;
  }
};

// Infant APIs
export const createInfant = async (data, token) => {
  console.log("data", data);
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/infant`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE INFANT ERROR", error);
    return error?.response;
  }
};

export const getAllInfants = async (params = {}, token) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/infant?${queryString}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET ALL INFANTS ERROR", error);
    return error?.response;
  }
};

export const getInfant = async (infantId, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/infant/${infantId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET INFANT ERROR", error);
    return error?.response;
  }
};

export const getInfantsByMsp = async (mspId, token) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/infant/msp/${mspId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET INFANTS BY MSP ERROR", error);
    return error?.response;
  }
};

export const updateInfant = async (infantId, data, token) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/infant/${infantId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE INFANT ERROR", error);
    return error?.response;
  }
};

export const deleteInfant = async (infantId, token) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/infant/${infantId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE INFANT ERROR", error);
    return error?.response;
  }
};

export const checkInInfant = async (infantId, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/infant/${infantId}/checkin`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CHECK IN INFANT ERROR", error);
    return error?.response;
  }
};

export const checkOutInfant = async (infantId, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/infant/${infantId}/checkout`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CHECK OUT INFANT ERROR", error);
    return error?.response;
  }
};

// Fingerprint Identification APIs
export const identifyFingerprint = async (fingerprintTemplate, fingerType = null) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/fingerprint/identify`,
      {
        fingerprintTemplate,
        fingerType, // 'left', 'right', or null for both
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("FINGERPRINT IDENTIFICATION ERROR", error);
    return error?.response;
  }
};

export const checkFingerprintMatcherHealth = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/fingerprint/health`);
    return res;
  } catch (error) {
    console.log("FINGERPRINT MATCHER HEALTH CHECK ERROR", error);
    return error?.response;
  }
};

// Staff Schedule APIs
export const getStaffSchedules = async ({ token, page = 1, limit = 50, staffId, startDate, endDate, sortBy = 'date', sortOrder = 'desc' } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (staffId) params.append('staffId', staffId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff-schedule?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET STAFF SCHEDULES ERROR", error);
    return error?.response;
  }
};

export const createStaffSchedule = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-schedule`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE STAFF SCHEDULE ERROR", error);
    return error?.response;
  }
};

export const updateStaffSchedule = async (scheduleId, data, token) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/staff-schedule/${scheduleId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE STAFF SCHEDULE ERROR", error);
    return error?.response;
  }
};

export const deleteStaffSchedule = async (scheduleId, token) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/staff-schedule/${scheduleId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE STAFF SCHEDULE ERROR", error);
    return error?.response;
  }
};

// Staff Attendance APIs
export const recordStaffAttendance = async (staffId, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-attendance/record/${staffId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("RECORD STAFF ATTENDANCE ERROR", error);
    return error?.response;
  }
};

// Filterable staff attendance list
export const getStaffAttendance = async ({ token, page = 1, limit = 50, staffId, startDate, endDate, status, sortBy = 'date', sortOrder = 'desc' } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (staffId) params.append('staffId', staffId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (status) params.append('status', status);
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff-attendance?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET STAFF ATTENDANCE ERROR", error);
    return error?.response;
  }
};

export const getTodayStaffAttendance = async ({ token, staffId } = {}) => {
  try {
    const params = new URLSearchParams();
    if (staffId) params.append('staffId', staffId);

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/staff-attendance/today?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET TODAY STAFF ATTENDANCE ERROR", error);
    return error?.response;
  }
};

export const checkInStaff = async ({ scheduleId }, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-attendance/checkin`,
      { scheduleId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CHECK IN STAFF ERROR", error);
    return error?.response;
  }
};

export const checkOutStaff = async ({ attendanceId }, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-attendance/checkout`,
      { attendanceId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CHECK OUT STAFF ERROR", error);
    return error?.response;
  }
};

export const temporaryCheckOutStaff = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-attendance/temporary-checkout`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("TEMPORARY CHECK OUT STAFF ERROR", error);
    return error?.response;
  }
};

export const temporaryCheckInStaff = async (data, token) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-attendance/temporary-checkin`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("TEMPORARY CHECK IN STAFF ERROR", error);
    return error?.response;
  }
};

// Tablet Collection API
export const markTabletCollected = async (userId, token) => {
  try {
    // URL-encode the userId to handle userIds with slashes (e.g., ISM/B1-26/D/0069)
    const encodedUserId = encodeURIComponent(userId);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${encodedUserId}/tablet-collected`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("MARK TABLET COLLECTED ERROR", error);
    return error?.response;
  }
};

export const getTabletCollectedUsers = async ({
  page = 1,
  limit = 50,
  cohortTag,
  issamCohortYear,
} = {}) => {
  try {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (cohortTag && String(cohortTag).trim()) {
      params.set("cohortTag", String(cohortTag).trim());
    }
    if (issamCohortYear && String(issamCohortYear).trim()) {
      params.set("issamCohortYear", String(issamCohortYear).trim());
    }
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/tablet-collected?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET TABLET COLLECTED USERS ERROR", error);
    return error?.response;
  }
};

// Issue Report API
export const submitIssueReport = async (data, token) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header only if token is provided (for anonymous reporting)
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/issue-report`,
      data,
      { headers }
    );
    return res;
  } catch (error) {
    console.log("SUBMIT ISSUE REPORT ERROR", error);
    return error?.response;
  }
};

export const getIssueReports = async ({
  token,
  page = 1,
  limit = 50,
  reportType,
  status,
  startDate,
  endDate,
  sortBy = "createdAt",
  sortOrder = "desc",
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (reportType) params.set("reportType", reportType);
    if (status) params.set("status", status);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);

    // Prefer axiosInstance so auth token + retry logic is consistent
    // (Authorization header is injected by interceptor).
    // If a token is explicitly provided, use a direct axios call.
    const res = token
      ? await axios.get(
          `${process.env.REACT_APP_API_URL}/issue-report?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      : await axiosInstance.get(`/issue-report?${params.toString()}`);
    return res;
  } catch (error) {
    console.log("GET ISSUE REPORTS ERROR", error);
    return error?.response;
  }
};

export const getIssueReportById = async ({ id, token }) => {
  try {
    const res = token
      ? await axios.get(`${process.env.REACT_APP_API_URL}/issue-report/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      : await axiosInstance.get(`/issue-report/${id}`);
    return res;
  } catch (error) {
    console.log("GET ISSUE REPORT ERROR", error);
    return error?.response;
  }
};

// Test scores API (MSP pre/post test summary for admin)
export const getMspTestScoresSummary = async ({
  page = 1,
  limit = 20,
  cohortTag,
  exportAll = false,
} = {}) => {
  try {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (cohortTag && String(cohortTag).trim()) {
      params.set("cohortTag", String(cohortTag).trim());
    }
    if (exportAll) {
      params.set("export", "1");
    }
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/test-scores/msp-summary?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET MSP TEST SCORES SUMMARY ERROR", error);
    return error?.response;
  }
};

// Food Poll API
export const getAllFoodPolls = async ({ mealType, date, isActive } = {}) => {
  try {
    const token = localStorage.getItem('token');
    let queryString = '';
    const params = [];
    
    if (mealType) params.push(`mealType=${encodeURIComponent(mealType)}`);
    if (date) params.push(`date=${encodeURIComponent(date)}`);
    if (isActive !== undefined) params.push(`isActive=${isActive}`);
    
    if (params.length > 0) {
      queryString = '?' + params.join('&');
    }

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/food-poll${queryString}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET ALL FOOD POLLS ERROR", error);
    return error?.response;
  }
};

export const getFoodPoll = async (pollId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/food-poll/${pollId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET FOOD POLL ERROR", error);
    return error?.response;
  }
};

export const getActiveFoodPoll = async ({ date, mealType }) => {
  try {
    const queryString = `?date=${encodeURIComponent(date)}&mealType=${encodeURIComponent(mealType)}`;
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/food-poll/active${queryString}`
    );
    return res;
  } catch (error) {
    console.log("GET ACTIVE FOOD POLL ERROR", error);
    return error?.response;
  }
};

export const createFoodPoll = async (data) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/food-poll`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("CREATE FOOD POLL ERROR", error);
    return error?.response;
  }
};

export const updateFoodPoll = async (pollId, data) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/food-poll/${pollId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("UPDATE FOOD POLL ERROR", error);
    return error?.response;
  }
};

export const deleteFoodPoll = async (pollId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/food-poll/${pollId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("DELETE FOOD POLL ERROR", error);
    return error?.response;
  }
};

export const voteOnFoodPoll = async (data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/food-poll/vote`,
      data
    );
    return res;
  } catch (error) {
    console.log("VOTE ON FOOD POLL ERROR", error);
    return error?.response;
  }
};

export const getFoodPollResults = async (pollId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/food-poll/${pollId}/results`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res;
  } catch (error) {
    console.log("GET FOOD POLL RESULTS ERROR", error);
    return error?.response;
  }
};

// =============================================
// REPORT SECTIONS
// =============================================

export const getReportSections = async (eventId, activeOnly = false) => {
  try {
    const params = new URLSearchParams();
    if (eventId) params.append('event', eventId);
    if (activeOnly) params.append('active', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await axiosInstance.get(`/report-section${query}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const createReportSection = async (data) => {
  try {
    const res = await axiosInstance.post('/report-section', data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateReportSection = async (id, data) => {
  try {
    const res = await axiosInstance.patch(`/report-section/${id}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const deleteReportSection = async (id) => {
  try {
    const res = await axiosInstance.delete(`/report-section/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

// =============================================
// REPORT TASKS
// =============================================

export const getReportTasks = async (eventId, sectionId, activeOnly = false) => {
  try {
    const params = new URLSearchParams();
    if (eventId) params.append('event', eventId);
    if (sectionId) params.append('section', sectionId);
    if (activeOnly) params.append('active', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await axiosInstance.get(`/report-task${query}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getReportTask = async (id) => {
  try {
    const res = await axiosInstance.get(`/report-task/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const createReportTask = async (data) => {
  try {
    const res = await axiosInstance.post('/report-task', data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateReportTask = async (id, data) => {
  try {
    const res = await axiosInstance.patch(`/report-task/${id}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const deleteReportTask = async (id) => {
  try {
    const res = await axiosInstance.delete(`/report-task/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

// =============================================
// REPORT EVENTS
// =============================================

export const getReportEvents = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await axiosInstance.get(`/report-event${query}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getReportEvent = async (id) => {
  try {
    const res = await axiosInstance.get(`/report-event/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const createReportEvent = async (data) => {
  try {
    const res = await axiosInstance.post('/report-event', data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateReportEvent = async (id, data) => {
  try {
    const res = await axiosInstance.patch(`/report-event/${id}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const deleteReportEvent = async (id) => {
  try {
    const res = await axiosInstance.delete(`/report-event/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

// =============================================
// REPORT SUBMISSIONS
// =============================================

export const getReportSubmissions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.event) params.append('event', filters.event);
    if (filters.task) params.append('task', filters.task);
    if (filters.user) params.append('user', filters.user);
    if (filters.status) params.append('status', filters.status);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await axiosInstance.get(`/report-submission${query}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getMyReportSubmissions = async (eventId) => {
  try {
    const query = eventId ? `?event=${eventId}` : '';
    const res = await axiosInstance.get(`/report-submission/my${query}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getReportProgress = async (eventId) => {
  try {
    const res = await axiosInstance.get(`/report-submission/progress?event=${eventId}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const upsertReportSubmission = async (data) => {
  try {
    const res = await axiosInstance.post('/report-submission', data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateReportSubmission = async (id, data) => {
  try {
    const res = await axiosInstance.patch(`/report-submission/${id}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const rejectReportSubmission = async (id, rejectionReason) => {
  try {
    const res = await axiosInstance.patch(`/report-submission/${id}/reject`, { rejectionReason });
    return res;
  } catch (error) {
    return error?.response;
  }
};

// ============ COOPERATIVES ============

export const getCooperatives = async (search) => {
  try {
    const params = {};
    if (search) params.search = search;
    const res = await axiosInstance.get('/cooperative', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const createCooperative = async (data) => {
  try {
    const res = await axiosInstance.post('/cooperative', data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateCooperative = async (id, data) => {
  try {
    const res = await axiosInstance.patch(`/cooperative/${id}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const deleteCooperative = async (id) => {
  try {
    const res = await axiosInstance.delete(`/cooperative/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getAttritionList = async ({
  page = 1,
  limit = 20,
  term,
  searchType,
  state,
  lga,
  cluster,
  role,
  cooperativeName,
  cooperativeId,
  year,
  startDate,
  endDate,
  sortBy,
  sortOrder,
} = {}) => {
  try {
    const params = { page, limit };
    if (term) {
      params.searchTerm = term;
      if (searchType) params.searchType = searchType;
    }
    if (state) params.state = state;
    if (lga) params.lga = lga;
    if (cluster) params.cluster = cluster;
    if (role) params.role = role;
    if (cooperativeName) params.cooperativeName = cooperativeName;
    if (cooperativeId) params.cooperativeId = cooperativeId;
    if (year !== undefined && year !== null && year !== "") params.year = year;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;

    const res = await axiosInstance.get("/cooperative/attrition", { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const downloadAttritionExcel = async (params = {}) => {
  try {
    const query = { ...params };
    delete query.page;
    delete query.limit;

    const res = await axiosInstance.get("/cooperative/attrition/export", {
      params: query,
      responseType: "blob",
    });
    return res;
  } catch (error) {
    return {
      status: error?.response?.status,
      data: error?.response?.data,
      errorDetails: error?.response?.data,
    };
  }
};

export const getStaffWithCooperatives = async (search, page = 1, limit = 50, hasAssignments = false) => {
  try {
    const params = { page, limit };
    if (search) params.search = search;
    if (hasAssignments) params.hasAssignments = '1';
    const res = await axiosInstance.get('/cooperative/staff', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const assignCooperativesToStaff = async (staffId, cooperativeIds) => {
  try {
    const res = await axiosInstance.patch('/cooperative/assign-staff', { staffId, cooperativeIds });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const addCooperativeMember = async (cooperativeId, data) => {
  try {
    const res = await axiosInstance.post(`/cooperative/${cooperativeId}/members`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const registerCooperativeMember = async (cooperativeId, data) => {
  try {
    const res = await axiosInstance.post(`/cooperative/${cooperativeId}/members/register`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateCooperativeMember = async (cooperativeId, memberId, data) => {
  try {
    const res = await axiosInstance.patch(`/cooperative/${cooperativeId}/members/${memberId}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const removeCooperativeMember = async (cooperativeId, memberId) => {
  try {
    const res = await axiosInstance.delete(`/cooperative/${cooperativeId}/members/${memberId}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

// ==================== Lookup ====================

export const lookupStaff = async (search, limit = 20) => {
  try {
    const params = { search, limit };
    const res = await axiosInstance.get('/staff/lookup', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const lookupMsp = async (search, limit = 20) => {
  try {
    const params = { search, limit };
    const res = await axiosInstance.get('/user/msp-lookup', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

// ============ COOP FORMS ============

export const getCoopForms = async () => {
  try {
    const res = await axiosInstance.get('/coop-form');
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getActiveCoopForm = async (state) => {
  try {
    const params = {};
    if (state !== undefined && state !== null) {
      params.state = state;
    }
    const res = await axiosInstance.get('/coop-form/active', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getActiveCoopForms = async () => {
  try {
    const res = await axiosInstance.get('/coop-form/active-all');
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const duplicateCoopForm = async (id) => {
  try {
    const res = await axiosInstance.post(`/coop-form/${id}/duplicate`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const createCoopForm = async (data) => {
  try {
    const res = await axiosInstance.post('/coop-form', data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const updateCoopForm = async (id, data) => {
  try {
    const res = await axiosInstance.patch(`/coop-form/${id}`, data);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const deleteCoopForm = async (id) => {
  try {
    const res = await axiosInstance.delete(`/coop-form/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

// ============ PAYSTACK (bank details fields) ============

export const getPaystackBanks = async () => {
  try {
    const res = await axiosInstance.get('/paystack/banks');
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const verifyPaystackBankAccount = async ({ account_number, bank_code }) => {
  try {
    const res = await axiosInstance.post('/paystack/verify-account', {
      account_number,
      bank_code,
    });
    return res;
  } catch (error) {
    return error?.response;
  }
};

// ============ COOP ENTRIES ============

export const getMyCoopEntries = async () => {
  try {
    const res = await axiosInstance.get('/coop-entry/my');
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const saveCoopEntry = async (
  formId,
  cooperativeId,
  values,
  submitFieldLabel,
  {
    year,
    month,
    week,
    day,
    submitFieldId,
    correctFieldLabel,
    correctFieldId,
    correctRowIndex,
    correctSubFieldLabel,
    correctSubFieldId,
    correctionValue,
    archivedLookupDetail,
    correctMemberId,
  } = {}
) => {
  try {
    const body = { formId, cooperativeId, values };
    if (year) body.year = year;
    if (month) body.month = month;
    if (week) body.week = week;
    if (day) body.day = day;
    if (submitFieldLabel) body.submitFieldLabel = submitFieldLabel;
    if (submitFieldId) body.submitFieldId = submitFieldId;
    if (correctFieldLabel || correctFieldId) {
      if (correctFieldLabel) body.correctFieldLabel = correctFieldLabel;
      if (correctFieldId) body.correctFieldId = correctFieldId;
      if (correctRowIndex != null) body.correctRowIndex = correctRowIndex;
      if (correctSubFieldLabel) body.correctSubFieldLabel = correctSubFieldLabel;
      if (correctSubFieldId) body.correctSubFieldId = correctSubFieldId;
      body.correctionValue = correctionValue;
      if (archivedLookupDetail) body.archivedLookupDetail = archivedLookupDetail;
      if (correctMemberId) body.correctMemberId = correctMemberId;
    }
    const res = await axiosInstance.post('/coop-entry', body);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const submitCoopEntry = async (entryId) => {
  try {
    const res = await axiosInstance.patch(`/coop-entry/${entryId}/submit`);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getCoopProgress = async (formId) => {
  try {
    const params = {};
    if (formId !== undefined && formId !== null && String(formId).trim() !== '') {
      params.formId = String(formId).trim();
    }
    const res = await axiosInstance.get('/coop-entry/progress', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

/** Admin: remove field from submitted labels so staff can edit again (cluster vs coop scope handled server-side). */
export const adminReopenCoopFieldForResubmission = async (body) => {
  try {
    const res = await axiosInstance.post('/coop-entry/admin/reopen-field', body);
    return res;
  } catch (error) {
    return error?.response;
  }
};

/** Admin: delete a question's stored answer; cluster+LGA vs per-coop scope handled server-side. */
export const adminClearCoopFieldAnswer = async (body) => {
  try {
    const res = await axiosInstance.post('/coop-entry/admin/clear-field-answer', body);
    return res;
  } catch (error) {
    return error?.response;
  }
};

/** Admin: clear submission locks on every entry for this form; values unchanged; all staff can edit and resubmit. */
export const adminReopenEntireCoopFormForAllStaff = async (body) => {
  try {
    const res = await axiosInstance.post('/coop-entry/admin/reopen-form-for-all-staff', body);
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getAllCoopEntries = async (params = {}) => {
  try {
    const res = await axiosInstance.get('/coop-entry', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

/** Triggers browser download of .xlsx (admin). Throws on error JSON body. */
export const downloadCoopSubmittedAnalysisExcel = async (
  formId,
  { year, month, enteredFrom, enteredTo } = {}
) => {
  const params = { year, month };
  if (enteredFrom) params.enteredFrom = enteredFrom;
  if (enteredTo) params.enteredTo = enteredTo;
  const res = await axiosInstance.get(`/coop-entry/export-submitted-analysis/${formId}`, {
    params,
    responseType: 'blob',
    timeout: 600000, // large exports can take several minutes
    'axios-retry': { retries: 0 },
  });
  const ct = (res.headers['content-type'] || '').toLowerCase();
  if (ct.includes('application/json')) {
    const text = await res.data.text();
    let msg = 'Export failed';
    try {
      msg = JSON.parse(text).message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  let filename = `coop-submitted-analysis.xlsx`;
  const cd = res.headers['content-disposition'];
  if (cd) {
    const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
    if (m) filename = decodeURIComponent(m[1].trim());
  }
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const getCoopEntryAuditLogs = async (params = {}) => {
  try {
    const res = await axiosInstance.get('/coop-entry/audit-logs', { params });
    return res;
  } catch (error) {
    return error?.response;
  }
};

export const getCoopEntryAuditLogDetail = async (id) => {
  try {
    const res = await axiosInstance.get(`/coop-entry/audit-logs/${id}`);
    return res;
  } catch (error) {
    return error?.response;
  }
};